"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "ui/lib/utils";
import { Button } from "ui/components/button";
import { X, Upload, FileText, Video, Image as ImageIcon } from "lucide-react";

const isFile = (value) =>
  typeof window !== "undefined" &&
  value instanceof Blob &&
  typeof value.type === "string";

const getAssetTypeFromMime = (mime = "") => {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "pdf";
  return "file";
};

export function FileUpload({
  value = null, // File | string | null
  onChange,
  accept = "*/*",
  maxSize = 20 * 1024 * 1024,
  disabled = false,
  showPreview = true,
  className,
  resolveFileUrl,
  fileType, // MIME type of the backend file (e.g. "video/mp4"), used for string values
}) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [assetType, setAssetType] = useState("file");
  const [error, setError] = useState(null);

  /* ---------- Resolve preview ---------- */
  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      setAssetType("file");
      return;
    }

    // New file
    if (isFile(value)) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      setAssetType(getAssetTypeFromMime(value.type));

      return () => URL.revokeObjectURL(url);
    }

    // Backend file ID
    if (typeof value === "string" && resolveFileUrl) {
      const url = resolveFileUrl(value);
      setPreviewUrl(url);
      // Backend ids carry no MIME type; use fileType when given, else assume image
      setAssetType(fileType ? getAssetTypeFromMime(fileType) : "image");
    }
  }, [value, resolveFileUrl, fileType]);

  /* ---------- Handlers ---------- */
  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxSize) {
      setError(
        `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`
      );
      return;
    }

    setError(null);
    onChange?.(file);
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setAssetType("file");
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
    onChange?.(null);
  };

  const renderPreview = () => {
    if (!previewUrl) return null;

    if (assetType === "image" || assetType.startsWith("image")) {
      return (
        <img
          src={previewUrl}
          alt="Preview"
          className="h-full w-full object-cover"
        />
      );
    }

    if (assetType === "video") {
      return (
        <video
          src={previewUrl}
          controls
          className="h-full w-full object-cover"
        />
      );
    }

    if (assetType === "pdf") {
      return (
        <div className="flex flex-col items-center justify-center gap-2">
          <FileText className="h-10 w-10 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">PDF Document</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-2">
        <ImageIcon className="h-10 w-10 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">File selected</span>
      </div>
    );
  };

  return (
    <div className={cn("space-y-2", className)}>
      {showPreview && previewUrl ? (
        <div className="relative flex size-60 items-center justify-center overflow-hidden rounded-lg border bg-muted">
          {renderPreview()}

          {!disabled && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={handleRemove}
              className="absolute right-1 top-1 h-7 w-7 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <label
          className={cn(
            "flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 text-sm transition hover:bg-muted",
            disabled && "pointer-events-none opacity-50"
          )}
        >
          <Upload className="mb-2 h-5 w-5 text-muted-foreground" />
          <span className="text-muted-foreground">Click to Upload File</span>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            disabled={disabled}
            onChange={handleSelect}
            className="hidden"
          />
        </label>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
